import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DashboardLayoutMain from "../../../layout/dashboard/DashboardLayoutMain";
import TabLink from "../../../components/fragment/tab-link/TabLink";
import RootState from "../../../redux/type";
import { ALL_ROUTES } from "../../../routes/AllRoutes";
import { hasRateIntelligenceRole } from "../../../utils/roles";
import ComparisonTab from "./components/ComparisonTab";
import HistoryTab from "./components/HistoryTab";
import AlertsTab from "./components/AlertsTab";
import "./rateIntelligence.css";

const TABS = ["Comparison", "History", "Alerts"] as const;
type RateIntelligenceTab = (typeof TABS)[number];

// History is built from the same comparison data, so it shares that permission.
const TAB_PERMISSION: Record<RateIntelligenceTab, string> = {
  Comparison: "view_rate_comparison",
  History: "view_rate_comparison",
  Alerts: "view_rate_alert",
};

export default function RateIntelligenceIndex() {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);

  // Per-tab permission AND the Super Admin/Finance role restriction both
  // apply — a tab-level permission alone isn't enough to bypass the role
  // gate, and the role alone isn't enough to bypass the per-tab permission.
  const allowedTabs = hasRateIntelligenceRole(user)
    ? TABS.filter((tab) => user?.permissions?.includes(TAB_PERMISSION[tab]))
    : [];

  const [activeTab, setActiveTab] = useState<RateIntelligenceTab>("Comparison");

  useEffect(() => {
    if (allowedTabs.length < 1 && user?.permissions?.length > 0) {
      navigate(ALL_ROUTES?.DASHBOARD_OVERVIEW);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.permissions]);

  // Fall back to the first tab the user can see (e.g. alerts-only users)
  const currentTab = allowedTabs.includes(activeTab)
    ? activeTab
    : allowedTabs[0];

  const tabLinkList = allowedTabs.map((label) => ({
    label,
    active: currentTab === label,
  }));

  return (
    <DashboardLayoutMain title="Rate Intelligence">
      <TabLink
        onClick={(param) => setActiveTab(param?.label as RateIntelligenceTab)}
        list={tabLinkList}
        activeBgTwo="#734792"
      />

      <section className="mx-[2rem] pb-[40px]">
        <div key={currentTab} className="rate-intel-tab-panel">
          {currentTab === "Comparison" && <ComparisonTab />}
          {currentTab === "History" && <HistoryTab />}
          {currentTab === "Alerts" && <AlertsTab />}
        </div>
      </section>
    </DashboardLayoutMain>
  );
}
